// src/components/events/EventsPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, SlidersHorizontal, X, Bike, Loader2 } from "lucide-react";
import { fetchPublicEvents } from "@/store/slices/eventsSlice";
import { INDIAN_STATES, EVENT_TYPES } from "@/utils/constants";
import EventCard from "./EventCard";
import SelectDropdown from "../common/SelectDropdown";

/* ── Skeleton card for loading state ────────────────── */
const SkeletonCard = () => (
  <div className="animate-pulse bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200/50 dark:border-gray-800 shadow">
    <div className="h-44 bg-gray-200 dark:bg-gray-800" />
    <div className="p-5 space-y-3">
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
      <div className="space-y-2 pt-2">
        <div className="h-3 bg-gray-100 dark:bg-gray-700/50 rounded w-2/3" />
        <div className="h-3 bg-gray-100 dark:bg-gray-700/50 rounded w-1/2" />
      </div>
      <div className="flex justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
        <div className="h-3 bg-gray-100 dark:bg-gray-700/50 rounded w-1/3" />
        <div className="h-3 bg-gray-100 dark:bg-gray-700/50 rounded w-1/4" />
      </div>
    </div>
  </div>
);

/* ── Event type filter pills ────────────────────────── */
const TYPE_FILTERS = [{ value: "", label: "All Events" }, ...EVENT_TYPES];

/* ── Main component ─────────────────────────────────── */
const EventsPage = () => {
  const dispatch = useDispatch();
  const { events, isLoading, error } = useSelector((state) => state.events);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial values from URL search params (restored on back-navigation)
  const searchTerm = searchParams.get("q") || "";
  const selectedType = searchParams.get("type") || "";
  const selectedState = searchParams.get("state") || "";
  const sortBy = searchParams.get("sort") || "upcoming";

  // Helper to update a single param (removes param when value is the default)
  const setParam = useCallback(
    (key, value, defaultValue = "") => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === defaultValue) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSearchTerm = (v) => setParam("q", v);
  const setSelectedType = (v) => setParam("type", v);
  const setSelectedState = (v) => setParam("state", v);
  const setSortBy = (v) => setParam("sort", v, "upcoming");

  // Fetch events on mount and when filters change
  useEffect(() => {
    dispatch(
      fetchPublicEvents({
        event_type: selectedType,
        state_code: selectedState,
        sort: sortBy,
      })
    );
  }, [dispatch, selectedType, selectedState, sortBy]);

  // Client-side search filter (title + description)
  const filteredEvents = useMemo(() => {
    if (!searchTerm.trim()) return events;
    const q = searchTerm.toLowerCase();
    return events.filter(
      (e) =>
        e.title?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q) ||
        e.location?.city?.toLowerCase().includes(q)
    );
  }, [events, searchTerm]);

  const activeFilterCount =
    (selectedType ? 1 : 0) + (selectedState ? 1 : 0) + (sortBy !== "upcoming" ? 1 : 0);

  const clearAllFilters = () => {
    setSearchParams({}, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      {/* ── Hero Section ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 dark:from-green-800 dark:via-emerald-800 dark:to-teal-900 py-16 md:py-20">
        {/* Animated background circles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
            transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
            className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
            transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
            className="absolute -bottom-20 -left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
              <Bike className="w-4 h-4 text-green-200" />
              <span className="text-sm font-medium text-green-100">
                Discover Rides Near You
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 tracking-tight">
              Cycling Events
            </h1>
            <p className="text-lg md:text-xl text-green-100/80 max-w-2xl mx-auto leading-relaxed">
              From city tours to mountain trails — find and join exciting cycling
              events happening across India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Search & Filters ── */}
      <section className="sticky top-16 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800 transition-colors duration-300">
        <div className="container mx-auto px-6 py-4">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="space-y-4"
          >
            {/* Top row: Search + State */}
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              {/* Search */}
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search events by name, city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 text-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* State dropdown */}
              <SelectDropdown
                id="state-filter"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                placeholder="All States"
                options={[
                  { value: "", label: "All States" },
                  ...INDIAN_STATES.filter((s) => s.code !== "").map((s) => ({
                    value: s.code,
                    label: s.name,
                  })),
                ]}
                className="w-full sm:w-52"
              />

              {/* Sort */}
              <SelectDropdown
                id="sort-filter"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={[
                  { value: "upcoming", label: "Upcoming First" },
                  { value: "recent", label: "Recently Added" },
                ]}
                className="w-full sm:w-48"
              />
            </div>

            {/* Event type pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {TYPE_FILTERS.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border ${
                    selectedType === type.value
                      ? "bg-green-600 text-white border-green-600 shadow-md shadow-green-500/20"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-green-400 hover:text-green-600 dark:hover:text-green-400"
                  }`}
                >
                  {type.label}
                </button>
              ))}

              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="shrink-0 px-3 py-1.5 rounded-full text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  Clear filters
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Events Grid ── */}
      <section className="py-10">
        <div className="container mx-auto px-6">
          {/* Results count */}
          {!isLoading && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-gray-500 dark:text-gray-400 mb-6"
            >
              {filteredEvents.length} event{filteredEvents.length !== 1 && "s"}{" "}
              found
              {searchTerm && ` for "${searchTerm}"`}
            </motion.p>
          )}

          {/* Loading skeletons */}
          {isLoading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          )}

          {/* Error state */}
          {!isLoading && error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                <X className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Something went wrong
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">{error}</p>
              <button
                onClick={() => dispatch(fetchPublicEvents({}))}
                className="px-6 py-2.5 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700 transition-colors"
              >
                Try Again
              </button>
            </motion.div>
          )}

          {/* Empty state */}
          {!isLoading && !error && filteredEvents.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                <Search className="w-10 h-10 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No events found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Try adjusting your search or filter criteria
              </p>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="px-5 py-2 rounded-xl bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors text-sm"
                >
                  Clear all filters
                </button>
              )}
            </motion.div>
          )}

          {/* Events grid */}
          {!isLoading && !error && filteredEvents.length > 0 && (
            <motion.div
              layout
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              <AnimatePresence mode="popLayout">
                {filteredEvents.map((event, index) => (
                  <EventCard
                    key={event._id}
                    event={event}
                    index={index}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
